import {
  AddSectionChartRequest,
  AddSectionTableRequest,
  AgentRawResponse,
  AgentRequest,
  AgentSession,
  AgentStreamAction,
  AgentStreamProgress,
  CreateAgentSessionRequest,
  CreateReportSectionRequest,
  PlotlyFigure,
  RemoveSectionChartRequest,
  RemoveSectionTableRequest,
  ReorderReportSectionsRequest,
  ReportWorkspaceMutationResult,
  SectionTable,
  SessionDataset,
  TokenTrace,
  UpdateAgentSessionRequest,
  UpdateReportSectionRequest,
  UpdateSessionDatasetRequest,
} from 'interfaces/agentSession';
import { resolveApiHost } from 'utils/api';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { sendAuthenticatedRequest } from 'utils/auth/authenticatedRequest';
import { downloadBlob } from 'utils/exports';

/** Data forwarded from a report_update SSE event during streaming. */
export interface ReportStreamUpdate {
  tool: string;
  section?: { id: string; title: string };
  /** Full session snapshot for instant query-cache update. */
  session?: AgentSession;
  /** Full chart data (including plotly figure) for chart preview in agent flow. */
  chart?: {
    id: string;
    title: string;
    chartType: string;
    plotlyFigure: PlotlyFigure;
  };
  /** Full table data for table preview in agent flow. */
  table?: SectionTable;
}

export const agentSessionsQueryKey = ['agentSessions'] as const;
export const MAX_SESSION_DATASET_FILE_BYTES = 15 * 1024 * 1024;
export const MAX_SESSION_DATASET_FILE_SIZE_LABEL = '15 MB';

export const getAgentSessions = (queryParams: string) => {
  return apiRequestHandlerPromise<AgentSession[]>({
    url: queryParams ? `agent/sessions?${queryParams}` : 'agent/sessions',
    method: 'GET',
  });
};

export const getAgentSession = (sessionId: string) => {
  return apiRequestHandlerPromise<AgentSession>({
    url: `agent/sessions/${sessionId}`,
    method: 'GET',
  });
};

export const createAgentSession = (request: CreateAgentSessionRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<AgentSession>({
    url: 'agent/sessions',
    method: 'POST',
    data,
  });
};

export const updateAgentSession = (sessionId: string, request: UpdateAgentSessionRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<AgentSession>({
    url: `agent/sessions/${sessionId}`,
    method: 'PUT',
    data,
  });
};

export const archiveAgentSession = (sessionId: string) => {
  return apiRequestHandlerPromise<AgentSession>({
    url: `agent/sessions/${sessionId}/archive`,
    method: 'POST',
  });
};

export const unarchiveAgentSession = (sessionId: string) => {
  return apiRequestHandlerPromise<AgentSession>({
    url: `agent/sessions/${sessionId}/unarchive`,
    method: 'POST',
  });
};

export const stopAgentSession = (sessionId: string) => {
  return apiRequestHandlerPromise<AgentSession>({
    url: `agent/sessions/${sessionId}/stop`,
    method: 'POST',
  });
};

export const listSessionDatasets = (sessionId: string) => {
  return apiRequestHandlerPromise<SessionDataset[]>({
    url: `agent/sessions/${sessionId}/datasets`,
    method: 'GET',
  });
};

export const uploadSessionDataset = (
  sessionId: string,
  file: File,
  metadata: {
    displayName?: string | null;
    description?: string | null;
    kind?: string | null;
    preferredJoinColumn?: string | null;
  } = {}
) => {
  if (file.size > MAX_SESSION_DATASET_FILE_BYTES) {
    throw new Error(`Dataset uploads are limited to ${MAX_SESSION_DATASET_FILE_SIZE_LABEL} per file`);
  }

  /**
   * We use FormData to upload the file along with its metadata, instead of sending JSON,
   * to better support larger files and avoid issues with JSON serialization of file data.
   * The server should be able to handle multipart/form-data requests for this endpoint accordingly.
   */
  const formData = new FormData();
  formData.append('file', file);

  if (metadata.displayName) {
    formData.append('displayName', metadata.displayName);
  }
  if (metadata.description) {
    formData.append('description', metadata.description);
  }
  if (metadata.kind) {
    formData.append('kind', metadata.kind);
  }
  if (metadata.preferredJoinColumn) {
    formData.append('preferredJoinColumn', metadata.preferredJoinColumn);
  }

  return apiRequestHandlerPromise<SessionDataset>({
    url: `agent/sessions/${sessionId}/datasets`,
    method: 'POST',
    data: formData,
  });
};

export const updateSessionDataset = (sessionId: string, datasetId: string, request: UpdateSessionDatasetRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<SessionDataset>({
    url: `agent/sessions/${sessionId}/datasets/${datasetId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteSessionDataset = (sessionId: string, datasetId: string) => {
  return apiRequestHandlerPromise<{ status: 'deleted'; datasetId: string }>({
    url: `agent/sessions/${sessionId}/datasets/${datasetId}`,
    method: 'DELETE',
  });
};

export const createReportSection = (sessionId: string, request: CreateReportSectionRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections`,
    method: 'POST',
    data,
  });
};

export const updateReportSection = (sessionId: string, sectionId: string, request: UpdateReportSectionRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections/${sectionId}`,
    method: 'PATCH',
    data,
  });
};

export const reorderReportSections = (sessionId: string, request: ReorderReportSectionsRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections/reorder`,
    method: 'POST',
    data,
  });
};

export const deleteReportSection = (sessionId: string, sectionId: string) => {
  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections/${sectionId}`,
    method: 'DELETE',
  });
};

export const addSectionChart = (sessionId: string, request: AddSectionChartRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections/${request.sectionId}/charts`,
    method: 'POST',
    data,
  });
};

export const removeSectionChart = (sessionId: string, request: RemoveSectionChartRequest) => {
  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections/${request.sectionId}/charts/${request.chartId}`,
    method: 'DELETE',
  });
};

export const addSectionTable = (sessionId: string, request: AddSectionTableRequest) => {
  const data = JSON.parse(JSON.stringify(request));

  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections/${request.sectionId}/tables`,
    method: 'POST',
    data,
  });
};

export const removeSectionTable = (sessionId: string, request: RemoveSectionTableRequest) => {
  return apiRequestHandlerPromise<ReportWorkspaceMutationResult>({
    url: `agent/sessions/${sessionId}/report/sections/${request.sectionId}/tables/${request.tableId}`,
    method: 'DELETE',
  });
};

export const sendAMessageToAgent = (agentRequest: AgentRequest) => {
  const data = JSON.parse(JSON.stringify(agentRequest));

  return apiRequestHandlerPromise<AgentRawResponse>({
    url: `agent/send`,
    method: 'POST',
    data,
  });
};

export const streamAgentMessage = async (
  agentRequest: AgentRequest,
  onDelta: (text: string) => void,
  signal?: AbortSignal,
  onStatus?: (text: string) => void,
  onProgress?: (progress: AgentStreamProgress) => void,
  onAction?: (action: AgentStreamAction) => void,
  onTokenTrace?: (trace: TokenTrace) => void,
  onFollowUpOptions?: (followUpOptions: AgentRawResponse['followUpOptions']) => void,
  onReportUpdate?: (update: ReportStreamUpdate) => void
): Promise<string> => {
  const url = `${resolveApiHost()}/agent/send-stream`;
  const requestBody = JSON.stringify({
    user_request: agentRequest.user_request,
    session_id: agentRequest.session_id,
    origin: agentRequest.origin,
    results_mode: agentRequest.results_mode,
  });

  const response = await sendAuthenticatedRequest({
    endpoint: '/agent/send-stream',
    execute: async (accessToken) => {
      const streamResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: requestBody,
        signal,
      });

      if (streamResponse.status === 401) {
        throw Object.assign(new Error('Unauthorized'), { response: { status: 401 } });
      }

      if (!streamResponse.ok) {
        const errorBody = await streamResponse.text();
        throw new Error(errorBody || `Stream request failed with status ${streamResponse.status}`);
      }

      return streamResponse;
    },
  });

  // Convert the response body into a reader so we can process chunks as they arrive.
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No readable stream in response');

  const decoder = new TextDecoder();
  let fullText = '';
  let buffer = '';
  let isStreamComplete = false;

  // Keep reading until the stream closes. Because chunks can split event lines in the
  // middle, we keep any incomplete trailing content in `buffer` for the next iteration.
  while (!isStreamComplete) {
    const { done, value } = await reader.read();
    if (done) {
      isStreamComplete = true;
      continue;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      // Ignore non-data SSE lines such as comments, empty lines, or future metadata.
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (!payload) continue;

      let parsed: {
        type?: string;
        text?: string;
        message?: string;
        scope?: string;
        stage?: string;
        progress?: number;
        action?: {
          id?: string;
          name?: string;
          label?: string;
          detail?: string | null;
          kind?: 'tool' | 'agent' | 'node';
          status?: 'running' | 'completed' | 'failed';
        };
        tokenTrace?: TokenTrace;
        followUpOptions?: AgentRawResponse['followUpOptions'];
        tool?: string;
        section?: Record<string, unknown>;
        session?: AgentSession;
        chart?: Record<string, unknown>;
        table?: Record<string, unknown>;
      } | null = null;
      try {
        // Each data line is expected to be a JSON object describing the next stream event.
        parsed = JSON.parse(payload);
      } catch {
        continue; // skip malformed JSON
      }

      // Surface server-side stream errors immediately so callers can stop rendering.
      if (parsed?.type === 'error') {
        throw new Error(parsed.message || 'Stream error from server');
      }

      // Status, progress, and action events are forwarded to optional UI callbacks so
      // the caller can show agent state updates independently from the text stream.
      if (parsed?.type === 'status' && parsed.text && onStatus) {
        onStatus(parsed.text);
      }
      if (
        parsed?.type === 'progress' &&
        typeof parsed.text === 'string' &&
        typeof parsed.scope === 'string' &&
        typeof parsed.stage === 'string' &&
        typeof parsed.progress === 'number' &&
        onProgress
      ) {
        onProgress({
          scope: parsed.scope,
          stage: parsed.stage,
          progress: parsed.progress,
          text: parsed.text,
        });
      }
      if (
        parsed?.type === 'action' &&
        parsed.action &&
        typeof parsed.action.id === 'string' &&
        typeof parsed.action.name === 'string' &&
        typeof parsed.action.label === 'string' &&
        typeof parsed.action.kind === 'string' &&
        typeof parsed.action.status === 'string' &&
        onAction
      ) {
        onAction({
          id: parsed.action.id,
          name: parsed.action.name,
          label: parsed.action.label,
          detail: typeof parsed.action.detail === 'string' ? parsed.action.detail : undefined,
          kind: parsed.action.kind,
          status: parsed.action.status,
        });
      }

      // Token trace events carry per-model-call and per-tool-call usage data for debugging.
      if (parsed?.type === 'token_trace' && parsed.tokenTrace && onTokenTrace) {
        onTokenTrace(parsed.tokenTrace);
      }

      if (parsed?.type === 'follow_up_options' && parsed.followUpOptions && onFollowUpOptions) {
        onFollowUpOptions(parsed.followUpOptions);
      }

      // Report update events signal that a report-mutation tool (e.g. create_report_section)
      // has completed.  The frontend uses this to refresh the report canvas in real time.
      if (parsed?.type === 'report_update' && onReportUpdate) {
        const update: ReportStreamUpdate = {
          tool: typeof parsed.tool === 'string' ? parsed.tool : 'unknown',
          section:
            parsed.section && typeof parsed.section === 'object'
              ? {
                  id: (parsed.section as Record<string, unknown>).id as string,
                  title: (parsed.section as Record<string, unknown>).title as string,
                }
              : undefined,
        };

        // Full session snapshot for instant cache update (no refetch round-trip).
        if (parsed.session && typeof parsed.session === 'object') {
          update.session = parsed.session as AgentSession;
        }

        // Full chart data for chart preview in agent flow.
        if (parsed.chart && typeof parsed.chart === 'object') {
          const chartData = parsed.chart as Record<string, unknown>;
          if (chartData.id && chartData.plotlyFigure) {
            update.chart = {
              id: chartData.id as string,
              title: (chartData.title as string) || '',
              chartType: (chartData.chartType as string) || '',
              plotlyFigure: chartData.plotlyFigure as PlotlyFigure,
            };
          }
        }

        // Full table data for table preview in agent flow.
        if (parsed.table && typeof parsed.table === 'object') {
          update.table = parsed.table as unknown as SectionTable;
        }

        onReportUpdate(update);
      }

      // Delta events contain the incremental assistant text. We append to the running
      // response and emit the full accumulated text so the UI can re-render in place.
      if (parsed?.type === 'delta' && parsed.text) {
        fullText += parsed.text;
        onDelta(fullText);
      }
    }
  }

  // Return the final assembled response once the stream ends.
  return fullText;
};

const SAFE_FILENAME_REGEX = /[^a-zA-Z0-9 _-]/g;

export const exportReportPptx = async (sessionId: string, reportTitle?: string) => {
  const blob = await apiRequestHandlerPromise<Blob>({
    method: 'GET',
    url: `agent/sessions/${sessionId}/report/export-pptx`,
    responseType: 'blob',
    mediaType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  });

  const safeName = (reportTitle || 'Analysis Report').replace(SAFE_FILENAME_REGEX, '').trim() || 'report';
  downloadBlob(blob, `${safeName}.pptx`);
};
